import { AxiosResponse } from 'axios';
import { call, CallEffect, PutEffect, select } from '@redux-saga/core/effects';
import { getCustomer, ResponseCardValidation } from './functions';
import { clearString, getFormattedCart, getFormattedDeviceFingerPrintID } from '@/utils/functions';
import { Action } from 'redux-saga';
import { ReducersTypes } from '@/redux/reducers/Models';
import { automaticApi } from '@/settings/api';
import apiPCI from '@/settings/apiPCI';
import environment from '@/settings/environment';
import { getIp } from '../ip/get';
import { PAYMENT_TYPE } from '@/redux/ducks/customer';

export interface Fetch {
  visitorId: string;
  card: {
    number: string;
    name: string;
    expiration: string;
  };
  selectedValue: {
    id: string;
    value: number;
    text: number;
    title: string;
    mandatory: boolean;
  } | null;
  mandatory: {
    id: string;
    value: number;
    text: number;
    title: string;
    mandatory: boolean;
  } | null;
  callback: (success: boolean, code?: string) => void;
}

export interface RechargeCardAction {
  type: string;
  payload: Fetch;
}

const uriPci = "/api/v1/tokenizing";
const uri = "/internalwebapi/v1/payments/credit-card";
const uriCardValidate = '/account/v1/validateCredictCard';

export default function* rechargeCard({
  payload,
}: RechargeCardAction): Generator<
  CallEffect<unknown> | PutEffect<Action>,
  void,
  AxiosResponse
> {
  try {
    // @ts-expect-error
    const customer: ReducersTypes['customerReducer'] = yield select(getCustomer);
    const client = customer?.client;
    const account = customer?.account;

    const journey = `APP-${environment?.api?.prefix?.toUpperCase() ?? ""}${account?.paymentType === PAYMENT_TYPE.IS_CARD ? "AlteracaoDadosCartao" : "AlteracaoFormaPagamento"}`;

    const card = payload.card;

    const responseEncryptedCard: AxiosResponse = yield call(apiPCI.post, uriPci, {
      dataToToken: clearString(card?.number),
    });

    const encryptedData = responseEncryptedCard?.data;

    const deviceFingerprintID = getFormattedDeviceFingerPrintID(payload.visitorId);

    // @ts-expect-error getIp
    const ip: string = yield call(getIp);

    const { total, selected } = getFormattedCart({
      debts: customer?.debts!,
      cart: customer?.cart?.filter(item => item.checked) ?? [],
    });

    const uriBrands = "/dominios/v1/financeiro/bandeiras";
    const responseBrands = yield call(automaticApi.get, uriBrands);

    const brand = responseBrands?.data?.listaBandeiraCartao?.find((item: {
      descricaoBandeira: string;
    }) => item?.descricaoBandeira === responseEncryptedCard?.data?.additionalInformation?.brand?.name);

    const address = client?.addresses?.find(address => address?.main) ?? client?.addresses?.[0];

    const responseCardValidation: ResponseCardValidation = yield call(
      automaticApi.post,
      uriCardValidate,
      {
        deviceFingerprintID,
        financialData: {
          value: total.toString(),
          creditCard: {
            flag: brand?.codigoBandeira,
            cardNumber: encryptedData?.token,
            monthDueDate: card?.expiration?.split("/")[0],
            yearDueDate: card?.expiration?.split("/")[1]
          },
        },
        client: {
          codeClient: client?.id,
          cpf: client?.document,
          phone: client?.phone,
          name: client?.name?.split(" ")[0],
          lastName: client?.name?.split(" ")?.[1],
          email: client?.email,
          registrationDate: client?.registrationDate,
          address: {
            street: `${address?.number}, ${address?.streetName}`,
            state: address?.state?.name!,
            country: "BR",
            city: address?.city,
            zipCode: address?.zipcode
          }
        },
        journey,
      }
    );

    const dataCardValidation = responseCardValidation?.data?.data;

    const body = {
      requestId: customer?.requestId!,
      customerIdentification: client?.id!,
      accountIdentification: account?.id!,
      paymentValue: total,
      licensePlate: customer?.selectedVehicles ? customer?.selectedVehicles?.toString() : customer?.vehicles?.[0]?.plate,
      tollPassages: selected,
      creditCardNumber: encryptedData?.token,
      creditCardValidationToken: dataCardValidation?.validationToken,
      creditCardOwner: card?.name,
      cardIdentifier: brand?.codigoBandeira,
      monthDueDate: Number(card?.expiration?.split("/")[0]),
      yearDueDate: Number(card?.expiration?.split("/")[1]),
      email: client?.email,
      phoneNumber: clearString(client?.phone!),
      ...(environment.api.prefix !== "qa" ? {
        deviceInformation: {
          fingerprintSessionId: deviceFingerprintID
        }
      } : null),
      userIP: ip,
      channel: environment.api.channel,
      companyIdentification: environment.api.companyIdentification,
    }

    const response: AxiosResponse = yield call(automaticApi.post, uri, body);
    const data = response?.data;
    if (data?.idTransaction) {
      yield call(
        payload.callback,
        true
      );
    } else {
      yield call(
        payload.callback,
        false
      );
    }
  } catch (error) {
    yield call(
      payload.callback,
      false
    );
  }
}
